// Webflow Navbar widget (.w-nav): a menu that collapses behind a hamburger button below the
// breakpoint in `data-collapse`. When open, Webflow shows a copy of the menu in a
// `.w-nav-overlay` under the bar; this does the same with a portal.

import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cx, type WfProps } from './element';

interface NavbarState {
  open: boolean;
  setOpen: (open: boolean) => void;
  overlay: HTMLElement | null;
  /** The open menu reports its height; Webflow sizes the overlay to match. */
  setOverlayHeight: (height: number | null) => void;
  duration: number;
  easing: string;
}

const NavbarContext = createContext<NavbarState | null>(null);
/** True for elements rendered inside the open-menu overlay. */
export const InNavOverlay = createContext(false);

export const useNavbar = () => useContext(NavbarContext);

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const EASINGS: Record<string, string> = {
  ease: 'ease',
  'ease-in': 'ease-in',
  'ease-out': 'ease-out',
  'ease-in-out': 'ease-in-out',
};

export function Navbar({ as: Tag = 'div', children, ...props }: WfProps) {
  const [open, setOpenState] = useState(false);
  const [overlay, setOverlay] = useState<HTMLElement | null>(null);
  const [overlayHeight, setOverlayHeight] = useState<number | null>(null);
  const root = useRef<HTMLElement>(null);
  const duration = Number(props['data-duration'] ?? 400);
  const easing = EASINGS[props['data-easing'] ?? 'ease'] ?? 'ease';

  const setOpen = useCallback((next: boolean) => setOpenState(next), []);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    // Close when the viewport grows past the collapse breakpoint (the button disappears).
    const onResize = () => {
      const button = root.current?.querySelector<HTMLElement>('.w-nav-button');
      if (button && getComputedStyle(button).display === 'none') setOpen(false);
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open, setOpen]);

  return (
    <NavbarContext.Provider value={{ open, setOpen, overlay, setOverlayHeight, duration, easing }}>
      <Tag ref={root} {...props}>
        {children}
        <div
          className="w-nav-overlay"
          data-wf-ignore=""
          ref={setOverlay}
          style={open ? { display: 'block', height: overlayHeight ?? undefined } : undefined}
        />
      </Tag>
    </NavbarContext.Provider>
  );
}

export function NavButton({ as: Tag = 'div', className, children, ...props }: WfProps) {
  const nav = useNavbar();
  return (
    <Tag
      {...props}
      className={cx(className, nav?.open && 'w--open')}
      role="button"
      tabIndex={0}
      aria-label="menu"
      aria-haspopup="menu"
      aria-expanded={nav?.open ?? false}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        nav?.setOpen(!nav.open);
      }}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          nav?.setOpen(!nav.open);
        }
      }}
    >
      {children}
    </Tag>
  );
}

export function NavMenu({ as: Tag = 'nav', children, ...props }: WfProps) {
  const nav = useNavbar();
  const menu = useRef<HTMLElement>(null);

  // Slide the overlay copy down from behind the bar, as Webflow's "default" animation does.
  useIsomorphicLayoutEffect(() => {
    if (!nav?.open || !menu.current) {
      nav?.setOverlayHeight(null);
      return;
    }
    const h = menu.current.getBoundingClientRect().height;
    nav.setOverlayHeight(h);
    menu.current.animate?.([{ transform: `translateY(-${h}px)` }, { transform: 'translateY(0)' }], {
      duration: nav.duration,
      easing: nav.easing,
    });
  }, [nav?.open]);

  const closeOnLinkClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('a[href]')) nav?.setOpen(false);
  };

  return (
    <>
      <Tag role="navigation" {...props}>
        {children}
      </Tag>
      {nav?.open &&
        nav.overlay &&
        createPortal(
          <InNavOverlay.Provider value={true}>
            <Tag role="navigation" {...props} ref={menu} data-nav-menu-open="" onClick={closeOnLinkClick}>
              {children}
            </Tag>
          </InNavOverlay.Provider>,
          nav.overlay,
        )}
    </>
  );
}
